// src/components/Interactive360.tsx
import React from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type Props = {
  frames: string[];
  ariaLabel?: string;
  fit?: "cover" | "contain";
};

const Interactive360: React.FC<Props> = ({
  frames,
  ariaLabel = "360 viewer",
  fit = "contain",
}) => {
  const [index, setIndex] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const clamp = (i: number) => (i + frames.length) % frames.length;

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current || frames.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const percent = relativeX / rect.width;
    const newIndex = Math.floor(percent * frames.length);
    setIndex(clamp(newIndex));
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current || frames.length === 0) return;
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = touch.clientX - rect.left;
    const percent = relativeX / rect.width;
    const newIndex = Math.floor(percent * frames.length);
    setIndex(clamp(newIndex));
  };

  const prev = () => {
    if (frames.length === 0) return;
    setIndex((i) => clamp(i - 1));
  };
  const next = () => {
    if (frames.length === 0) return;
    setIndex((i) => clamp(i + 1));
  };

  if (!frames || frames.length === 0) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        userSelect: "none",
        overflow: "hidden",
        cursor: "ew-resize",
      }}
    >
      <Box
        component="img"
        loading="lazy"
        decoding="async"
        src={frames[index]}
        alt={ariaLabel}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: fit,
          display: "block",
        }}
      />

      {/* left/right manual buttons */}
      <IconButton
        onClick={prev}
        sx={{
          position: "absolute",
          left: 6,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: "rgba(255,255,255,0.75)",
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        onClick={next}
        sx={{
          position: "absolute",
          right: 6,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: "rgba(255,255,255,0.75)",
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default Interactive360;
